import { useCallback, useMemo, useEffect } from "react";
import { GetGameViewById, SubmitPlayerGameVotes, VoteType } from "@grassrootly/api";
import { useUser } from "../../api/auth/AuthSelector";
import { useGameView } from "../../api/game/GameViewSelector";
import { useAppDispatch, useAlert } from "@grassrootly/rn-core";
import { gameViewActions } from "../../api/game/GameViewReducer";
import { adaptGameViewToGame } from "../game/gameViewAdapter";
import useBaseRequest from "../../api/BaseRequest";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { RootStackParamList } from "../../navigation/Navigation.types";

/**
 * useGameVoteTraining - Hook for TRAINING game voting with opponent/teammate split
 * 
 * Players vote twice per category:
 * - Once for best opponent (from other team)
 * - Once for best teammate (from same team)
 * 
 * Vote types remain the same: "creativity", "defense", "effort"
 * Teams split by HOST/VISITOR using the "team" field
 */
export const useGameVoteTraining = (
  gameId: number,
  navigation: NativeStackNavigationProp<RootStackParamList, "vote">
) => {
  const user = useUser();
  const dispatch = useAppDispatch();
  const gameView = useGameView(gameId);
  const game = gameView ? adaptGameViewToGame(gameView) : null;
  const { danger } = useAlert();

  // Function to refresh game data after voting
  const { execute: refreshGameView, loading: fetching } = useBaseRequest(GetGameViewById, {
    onCompleted: (data) => {
      if (!data) return;
      dispatch(gameViewActions.setGameView(data));
    },
  });

  const fetchGame = () => {
    refreshGameView({ id: gameId });
  };

  // Fetch game data on mount if not already loaded
  useEffect(() => {
    if (!gameView && !fetching) {
      fetchGame();
    }
  }, [gameId, gameView, fetching]);

  const { execute, loading: submitting } = useBaseRequest(SubmitPlayerGameVotes, {
    onCompleted: (data) => {
      fetchGame();
      navigation.goBack();
    },
    onError: (error) => {
      console.error('Vote submission error:', error);
      fetchGame();
      navigation.goBack();
    },
  });

  const hasAttended = useCallback(() => {
    return !!game?.attendees?.find((a) => a.player_id === user?.id && a.attended);
  }, [game, user]);

  // Get current player's attendance to determine their team
  const currentPlayerAttendance = useMemo(() => {
    return game?.attendees?.find((a) => a.player_id === user?.id);
  }, [game, user]);

  // Filter opponents (players from the other team)
  const opponents = useMemo(() => {
    if (!game || !currentPlayerAttendance) return [];
    
    const currentTeam = currentPlayerAttendance.team;
    
    return game.attendees.filter((a) => {
      if (!a.attended || a.player_id === user?.id) return false;
      
      // Filter by team (HOST/VISITOR)
      if (currentTeam && a.team) {
        return a.team !== currentTeam;
      }
      
      return false;
    });
  }, [game, currentPlayerAttendance, user]);

  // Filter teammates (players from the same team, excluding self)
  const teammates = useMemo(() => {
    if (!game || !currentPlayerAttendance) return [];
    
    const currentTeam = currentPlayerAttendance.team;
    
    return game.attendees.filter((a) => {
      if (!a.attended || a.player_id === user?.id) return false;
      
      // Filter by team (HOST/VISITOR)
      if (currentTeam && a.team) {
        return a.team === currentTeam;
      }
      
      return false;
    });
  }, [game, currentPlayerAttendance, user]);

  const submitVotes = (votes: Array<{ type: VoteType; attendanceId: number | null }>) => {
    if (submitting) {
      return;
    }
    
    if (!hasAttended()) {
      danger({ message: "Only players that have played the game can vote.", title: "Not allowed" });
      return;
    }

    // Filter out null votes (skipped votes) and ensure type safety
    const validVotes = votes
      .filter((v): v is { type: VoteType; attendanceId: number } => v.attendanceId !== null);

    execute({
      votes: validVotes.map((v) => ({ ...v, id: user?.id! })),
    });
  };

  const goBack = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
      return;
    }
    navigation.replace("main");
  };

  return {
    game,
    opponents,
    teammates,
    submitVotes,
    saving: fetching || submitting,
    goBack,
  };
};
